import React, { useState } from 'react';
import { bookingPaymentService, formatCurrency } from '../../services/bookingPaymentService';
import './VoucherPayPalButton.css'; // Reuse existing button styles

interface BookingStripeButtonProps {
  bookingId: number;
  amount: number;
  onSuccess?: (result: { bookingId: number; amount: number }) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
}

/**
 * Stripe Button Component for Booking Payments
 *
 * This component handles the Stripe payment flow for bookings:
 * 1. Creates a Stripe Checkout session via our backend (/api/payment/initiate)
 * 2. Redirects to Stripe Checkout for payment
 * 3. (Return page handles status verification)
 */
const BookingStripeButton: React.FC<BookingStripeButtonProps> = ({
  bookingId,
  amount,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleStripeClick = async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);

    try {
      const baseUrl = window.location.origin;
      const returnUrl = `${baseUrl}/buchung/success?bookingId=${bookingId}&provider=stripe`;
      const cancelUrl = `${baseUrl}/checkout/${bookingId}?cancelled=true`;

      console.log('Initiating Stripe payment for booking:', bookingId);

      const result = await bookingPaymentService.initiateStripePayment(
        bookingId,
        returnUrl,
        cancelUrl
      );

      console.log('Stripe initiation result:', result);

      if (result.success && result.paymentUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.paymentUrl;
      } else {
        onError(result.errorMessage || 'Konnte Stripe-Zahlung nicht starten');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('Stripe payment error:', err);
      onError(err.message || 'Fehler beim Starten der Stripe-Zahlung');
      setIsLoading(false);
    }
  };

  return (
    <div className="voucher-paypal-button-container booking-stripe-button-container">
      <button
        className="voucher-paypal-button booking-stripe-button"
        onClick={handleStripeClick}
        disabled={isLoading || disabled}
        style={{ background: 'linear-gradient(135deg, #635bff 0%, #4f46e5 100%)' }}
      >
        {isLoading ? (
          <span className="paypal-loading">
            <span className="spinner"></span>
            Wird verarbeitet...
          </span>
        ) : (
          <>
            <span className="stripe-logo-text" style={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>
              Kreditkarte
            </span>
            <span className="paypal-amount">{formatCurrency(amount)}</span>
          </>
        )}
      </button>
      <p className="paypal-secure-note">
        <span className="secure-icon">🔒</span>
        Sichere Zahlung mit 3D Secure
      </p>
    </div>
  );
};

export default BookingStripeButton;
